<script setup>
import { computed, ref, onUnmounted, onMounted } from 'vue'
import { useCartStore } from '../stores/cartStore'

const cartStore = useCartStore()

const stateFee = computed(() => {
  if (cartStore.products.length === 0) return undefined

  const product = cartStore.products.find((product) =>
    product.name.includes('State Filing Fee')
  )
  return product ? product : undefined
})

const ccProduct = computed(() => {
  if (cartStore.products.length === 0) return undefined
  return cartStore.products.filter(
    (product) => !product.name.includes('State Filing Fee')
  )[0]
})

// Target time in minutes
const targetMinutes = 25 // for example, 1 minute from now
const endTime = new Date(new Date().getTime() + targetMinutes * 60000)

// Countdown value in seconds
const countdown = ref((endTime - new Date()) / 1000)

// Update the countdown every second
const updateCountdown = () => {
  const secondsLeft = (endTime - new Date()) / 1000
  countdown.value = secondsLeft > 0 ? secondsLeft : 0
}

let intervalId = null

onMounted(() => {
  intervalId = setInterval(updateCountdown, 1000)
})

onUnmounted(() => {
  clearInterval(intervalId)
})

// Convert seconds to minutes for display
const displayMinutes = () => {
  const minutes = Math.floor(countdown.value / 60)
  const seconds = Math.floor(countdown.value % 60)
  return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
}
</script>
<template>
  <div class="ps-lg-2 px-auto">
    <div class="card card-sm shadow-none mb-4 border">
      <div class="card-body">
        <div class="border-bottom pb-4 mb-4">
          <h3 class="card-header-title">Order summary</h3>
        </div>

        <!-- Total -->
        <div class="pb-4 border-bottom mb-4">
          <div class="d-grid">
            <dl class="row mb-0">
              <dt class="col-8">Item Subtotal (2)</dt>
              <dd class="col-4 text-sm-end mb-0">
                ${{ cartStore.totalPriceNoDiscount + 50 }}
              </dd>
            </dl>
            <div class="d-grid border-bottom mb-4">
              <div class="col text-body">
                <!-- Accordian -->
                <div
                  class="accordion accordion-flush accordion-btn-icon-start mt-n1"
                  id="accordionFlushExample"
                >
                  <div class="accordion-item">
                    <div class="accordion-header" id="flush-headingOne">
                      <a
                        class="accordion-button collapsed order-summary-accordion"
                        role="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#flush-collapseOne"
                        aria-expanded="false"
                        aria-controls="flush-collapseOne"
                      >
                        <small>Order Details</small>
                      </a>
                    </div>
                    <div
                      id="flush-collapseOne"
                      class="accordion-collapse collapse"
                      aria-labelledby="flush-headingOne"
                      data-bs-parent="#accordionFlushExample"
                    >
                      <div class="accordion-body">
                        <!-- Product -->
                        <div class="border-bottom border-top pt-4 pb-4 mb-4">
                          <div class="d-flex">
                            <div class="flex-shrink-0"></div>

                            <div class="flex-grow-1">
                              <dl class="row">
                                <dt class="col-8">FL State Fee</dt>
                                <dd class="col-4 text-sm-end mb-0 float-end">
                                  ${{ stateFee.price / 100 }}
                                </dd>
                              </dl>
                              <div class="d-grid">
                                <div class="text-body">
                                  <span class="small"
                                    >Collected by the State of Florida
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <!-- End Product -->

                        <!-- Product -->
                        <div class="pb-4">
                          <div class="d-flex">
                            <div class="flex-shrink-0"></div>

                            <div class="flex-grow-1">
                              <dl class="row">
                                <dt class="col-8">{{ ccProduct.name }}</dt>
                                <dd class="col-4 text-sm-end mb-0 float-end">
                                  <del
                                    >${{ ccProduct.price / 100 + 50
                                    }}<span
                                      class="small"
                                      v-if="
                                        ccProduct.name.includes(
                                          'ComplianceClerk Pro'
                                        )
                                      "
                                      >/yr</span
                                    ></del
                                  >
                                  ${{ ccProduct.price / 100
                                  }}<span
                                    class="small"
                                    v-if="
                                      ccProduct.name.includes(
                                        'ComplianceClerk Pro'
                                      )
                                    "
                                    >/yr</span
                                  >
                                </dd>
                              </dl>
                              <div class="d-grid">
                                <template
                                  v-if="
                                    ccProduct.name.includes(
                                      'ComplianceClerk Pro'
                                    )
                                  "
                                >
                                  <div class="text-body text-primary mb-3">
                                    <span class="small"
                                      >File today and save on your Beneficial
                                      Ownership Report (a new Federal
                                      requirement from FinCEN).</span
                                    >
                                  </div>
                                  <div class="px-auto small">
                                    <ul
                                      class="list-checked list-checked-soft-bg-primary"
                                    >
                                      <li class="list-checked-item">
                                        Annual Report <del>$99</del>
                                        <span
                                          class="badge bg-soft-primary text-primary"
                                          >Included!</span
                                        >
                                      </li>
                                      <li class="list-checked-item">
                                        Beneficial Ownership Report
                                        <del>$149</del>
                                        <span
                                          class="badge bg-soft-primary text-primary"
                                          >Included!</span
                                        >
                                      </li>
                                      <li class="list-checked-item">
                                        $250 in value for only ${{
                                          ccProduct.price / 100
                                        }}/yr!
                                      </li>
                                      <li class="list-checked-item">
                                        Avoid Penalties and Never Miss a Filing
                                      </li>
                                      <li class="list-checked-item">
                                        No Upsells or Hidden Fees
                                      </li>
                                    </ul>
                                  </div>
                                </template>
                                <template v-else>
                                  <div class="text-body">
                                    <span class="small">{{
                                      ccProduct.description
                                    }}</span>
                                  </div>
                                </template>
                              </div>
                            </div>
                          </div>
                        </div>
                        <!-- End Product -->
                      </div>
                    </div>
                  </div>
                </div>
                <!-- End Accordian -->
              </div>
            </div>
            <dl class="row mb-0">
              <dt class="col-8">$50 Off - Early Bird Discount</dt>
              <dd class="col-4 text-sm-end mb-0">($50)</dd>
            </dl>
            <div class="d-grid">
              <div class="text-body">
                <span class="small">
                  <span class="badge bg-soft-danger text-danger">{{
                    displayMinutes()
                  }}</span>
                  minutes left to lock in your discount.</span
                >
              </div>
            </div>
            <!-- End Row -->
          </div>
        </div>
        <!-- End Total -->
        <!-- Discount -->
        <div
          v-if="
            cartStore.discount.amount > 0 || cartStore.discount.percent_off > 0
          "
          class="border-bottom pb-4 mb-4"
        >
          <div class="d-flex">
            <div class="flex-shrink-0"></div>

            <div class="flex-grow-1">
              <dl class="row">
                <dt class="col-8">Discount</dt>
                <dd class="col-4 text-sm-end mb-0 float-end">
                  <template v-if="cartStore.discount.amount > 0">
                    (${{ cartStore.discount.amount / 100 }})
                  </template>
                  <template v-else-if="cartStore.discount.percent_off > 0">
                    {{ cartStore.discount.percent_off }}% off*
                  </template>
                </dd>
              </dl>
              <div class="d-grid">
                <div class="text-body">
                  <span class="small"
                    >{{ cartStore.discount.description }}
                  </span>
                  <template v-if="cartStore.discount.percent_off > 0">
                    <small style="font-style: italic; display: block"
                      >*% discounts do not apply to state fees</small
                    >
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- End Discount -->
        <!-- Due Today -->
        <div class="pb-4">
          <div class="d-grid">
            <dl class="row">
              <dt class="col-8">Due Today</dt>
              <dd class="col-4 text-sm-end mb-0">
                <strong>${{ cartStore.totalPrice.toFixed(2) }}</strong>
              </dd>
            </dl>
            <!-- End Row -->
          </div>
          <div class="d-grid">
            <div class="text-body">
              <span class="small"
                >Next Year: ${{ ccProduct.price / 100 }} + state fees</span
              >
            </div>
          </div>
        </div>
        <!-- End Due Today -->
      </div>
      <!-- End Card Body -->
    </div>
    <!-- End Card -->
  </div>
</template>
